import React from 'react';
import placeholder from 'images/placeholder.webp';
import { Property } from '../../domain/entities/Property';
import {
  formatPrice,
  formatPricePerSqft,
  formatSqFt,
  getMarketTimeText,
} from '../../../../shared/utils/formatters/propertyFormatters';
import { useBreakpoint } from '../../../../shared/hooks/useBreakpoint';
import { useHoverStore } from '../../../public-map/application/hoverStore';

export function SplitViewCard({ property }: { property: Property }) {
  const { isXs } = useBreakpoint();
  const {
    setHoveredPropertyId,
    isPropertyHovered,
    isPropertyInHoveredCluster,
  } = useHoverStore();

  const isSingleHover =
    isPropertyHovered(property.id) && !isPropertyInHoveredCluster(property.id);
  const isInCluster = isPropertyInHoveredCluster(property.id);

  const handleMouseEnter = () => setHoveredPropertyId(property.id);
  const handleMouseLeave = () => setHoveredPropertyId(null);

  // Apply highlight classes based on hover state
  const highlightClass = isSingleHover
    ? '!bg-indigo-50 '
    : isInCluster
    ? '!bg-indigo-50 '
    : '';

  if (isXs) {
    return (
      <article
        id="split-view-card-mobile"
        className={`rounded-lg shadow-sm border bg-white flex max-h-[300px] h-full w-full transition-all duration-150 ${highlightClass}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => window.open(`/properties/${property.id}`, '_blank')}
      >
        <img
          src={property.imgUrl ?? placeholder}
          alt={property.address ?? 'Address Unknown'}
          className="w-[100px] sm:w-[150px] object-cover rounded-l-lg rounded-r-none"
        />

        <div
          id="split-view-card-content"
          className="px-2 py-3 flex flex-col w-full"
        >
          <div
            id="split-view-card-content-header"
            className="flex flex-col justify-between gap-2 pb-2 border-b-2 border-gray-200"
          >
            <div
              id="spvc-content-header-address"
              className="font-semibold text-sm flex-1 text-center"
            >
              {property.address ?? '—'}
            </div>
            <div
              id="spvc-content-header-specs"
              className="text-xs font-bold text-gray-600 flex flex-1 items-center justify-evenly flex-row"
            >
              <div id="spvc-content-header-specs-beds">
                {property.beds || '-'} beds
              </div>
              <div id="spvc-content-header-specs-baths">
                {property.baths || '-'} baths
              </div>
              <div id="spvc-content-header-specs-sqft">
                {formatSqFt(property.sqft) || '- sq ft'}
              </div>
            </div>
          </div>

          <div id="split-view-card-content-body" className="pt-2 h-full px-2">
            <div
              id="split-view-card-content-body-left"
              className="grid grid-cols-[1fr,2fr] gap-y-1 w-full text-sm items-center"
            >
              {/* List Price */}
              <div className="text-xs text-gray-500 uppercase ">List Price</div>
              <div className="text-lg font-medium text-gray-900 text-end">
                {formatPrice(property.listPrice)}
              </div>

              {/* Price per sq ft */}
              <div className="text-xs text-gray-400 uppercase">Price/SqFt</div>
              <div className="text-lg font-medium text-gray-900 text-end">
                {formatPricePerSqft(property.listPricePerSqft)}
              </div>

              {/* Potential Equity */}
              <div className="text-xs text-gray-400 uppercase">
                Potential Equity
              </div>
              <div className="text-xl font-medium text-gray-900 text-end">
                {formatPrice(property.equity)}
              </div>

              {/* On Market / Sold */}
              <div className="text-xs text-gray-400 uppercase col-span-2 pt-2 text-center">
                {getMarketTimeText(
                  property.status,
                  property.daysOnMarket,
                  property.daysSinceSold
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  }
  return (
    <article
      id="split-view-card-desktop"
      className={`flex cursor-pointer rounded-lg shadow-sm border bg-white h-[220px] transition-all duration-150 ${highlightClass}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => window.open(`/properties/${property.id}`, '_blank')}
    >
      <img
        src={property.imgUrl ?? placeholder}
        alt={property.address ?? 'Address Unknown'}
        className="w-[260px] h-[200px] object-cover rounded-l-lg rounded-r-none flex-shrink-0"
      />

      <div
        id="split-view-card-content"
        className="px-6 py-3 flex flex-col flex-1 min-w-0 sm:w-[475px]"
      >
        <div
          id="split-view-card-content-header"
          className="flex justify-between gap-2 pb-2 border-b-2 border-gray-200"
        >
          <div
            id="spvc-content-header-address"
            className="font-semibold text-sm flex-1"
          >
            {property.address ?? '—'}
          </div>
          <div
            id="spvc-content-header-specs"
            className="text-xs font-bold text-gray-600 flex flex-1 items-center justify-evenly flex-row"
          >
            <div id="spvc-content-header-specs-beds">
              {property.beds || '-'} beds
            </div>
            <div id="spvc-content-header-specs-baths">
              {property.baths || '-'} baths
            </div>
            <div id="spvc-content-header-specs-sqft">
              {formatSqFt(property.sqft) || '- sq ft'}
            </div>
          </div>
        </div>

        <div
          id="split-view-card-content-body"
          className="pt-2 flex items-center h-full"
        >
          {/* Left block */}
          <div
            id="split-view-card-content-body-left"
            className="basis-1/3 grid grid-rows-2 h-full pr-6 border-r-2 border-gray-200"
          >
            <div
              id="split-view-card-content-body-left-top"
              className="flex flex-col px-2 py-1 justify-center"
            >
              <div className="text-xs text-gray-500 uppercase">List Price</div>
              <div className="text-xl font-bold">
                {formatPrice(property.listPrice)}
              </div>
            </div>
            <div
              id="split-view-card-content-body-left-bottom"
              className="flex flex-col px-2 py-1 justify-center"
            >
              <div className="text-xs text-gray-400 uppercase">Price/SqFt</div>
              <div
                id="svc-content-body-price-per-sqft"
                className="text-xl font-bold"
              >
                {formatPricePerSqft(property.listPricePerSqft)}
              </div>
            </div>
          </div>

          {/* Right block */}
          <div
            id="split-view-card-content-body-right"
            className="basis-2/3 flex flex-col justify-center items-center h-full"
          >
            <div className="text-xs text-gray-500 uppercase">
              Potential Equity
            </div>
            <div className="text-3xl font-bold text-gray-700 font-inter">
              {formatPrice(property.equity)}
            </div>
            <div className="text-xs text-gray-400 pt-4 uppercase">
              {getMarketTimeText(
                property.status,
                property.daysOnMarket,
                property.daysSinceSold
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
